/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量编辑水印编辑器信息架构实验分桶
 */

export type BatchWatermarkEditorClarityVariant = 'control' | 'clarity_v1'

const EXPERIMENT_SALT = 'batch_watermark_editor_clarity_v1'
export const BATCH_WATERMARK_EDITOR_CLARITY_CONFIG_KEY =
  'batch_watermark_editor_clarity_v1'
export const BATCH_WATERMARK_EDITOR_CLARITY_QUERY_KEY =
  'batch_editor_clarity_variant'

interface ResolveVariantOptions {
  rolloutPercentage?: number
  overrideVariant?: string | null
  allowOverride?: boolean
}

const isVariant = (
  value: string | null | undefined
): value is BatchWatermarkEditorClarityVariant =>
  value === 'control' || value === 'clarity_v1'

/**
 * 将入口页中的合法验收参数透传到编辑器。
 *
 * @param sourceSearch - 入口页查询参数
 * @param targetSearch - 已由其他编辑器实验生成的查询参数
 * @returns 合并后的编辑器查询参数
 */
export const appendEditorClarityOverrideSearch = (
  sourceSearch: string,
  targetSearch = ''
): string => {
  const variant = new URLSearchParams(sourceSearch).get(
    BATCH_WATERMARK_EDITOR_CLARITY_QUERY_KEY
  )
  if (!isVariant(variant)) {
    return targetSearch
  }

  const targetSearchParams = new URLSearchParams(targetSearch)
  targetSearchParams.set(BATCH_WATERMARK_EDITOR_CLARITY_QUERY_KEY, variant)
  return `?${targetSearchParams.toString()}`
}

/** 按团队短 ID 生成稳定的 0～99 实验桶。 */
export const getEditorClarityBucket = (teamShortId: string): number => {
  const source = `${EXPERIMENT_SALT}:${teamShortId.trim()}`
  let hash = 5381

  for (let index = 0; index < source.length; index += 1) {
    hash = (hash * 33) ^ source.charCodeAt(index)
  }

  return (hash >>> 0) % 100
}

/** 解析当前团队的编辑器信息架构实验版本。 */
export const resolveEditorClarityVariant = (
  teamShortId: string | undefined,
  options: ResolveVariantOptions = {}
): BatchWatermarkEditorClarityVariant => {
  const {
    rolloutPercentage = 0,
    overrideVariant,
    allowOverride = false
  } = options

  if (allowOverride && isVariant(overrideVariant)) {
    return overrideVariant
  }

  const normalizedTeamShortId = teamShortId?.trim()
  if (!normalizedTeamShortId) {
    return 'control'
  }

  return getEditorClarityBucket(normalizedTeamShortId) <
    Math.min(100, Math.max(0, Math.floor(rolloutPercentage)))
    ? 'clarity_v1'
    : 'control'
}
